<template>
  <v-dialog v-model="isActive" fullscreen hide-overlay transition="dialog-bottom-transition" scrollable>
    <v-overlay v-if="isLoading" absolute color="#48B38B">
      <v-progress-circular indeterminate size="64"></v-progress-circular>
    </v-overlay>

    <div class="budget-details">
      <div class="top-bar">
        <div @click="close" class="action">
          <v-icon class="px-1">mdi-arrow-left</v-icon>
        </div>
        <div style="font-size: 20px; margin-top: 15px; margin-left: 5px; float: left; letter-spacing: 1px; text-transform: uppercase">
          {{ titlePrefix }} orçamento
        </div>
      </div>
      <div v-if="selectedBudget?.client" class="basic-information">
        <div class="client-info">
          <div class="client-details">
            <div class="client-name">{{ selectedBudget.client.name }}</div>
            <div class="client-vehicle">{{ selectedBudget.vehicle ? selectedBudget.vehicle.description : 'Veículo não informado' }}</div>
          </div>
          <div>
            <div v-if="selectedBudget.vehicle" class="vehicle-plate">
              <img :src="selectedBudget.vehicle.vehicle_plate_photo_url" />
            </div>
          </div>
        </div>
      </div>
      <div class="basic-information">
        <v-dialog v-model="isDatePickerOpen" persistent width="290px">
          <template v-slot:activator="{ on, attrs }">
            <v-text-field
              class="ma-0 pt-0"
              v-model="formattedSelectedDate"
              label="Data"
              readonly
              append-icon="mdi-calendar"
              hide-details
              outlined
              @blur="selectedDate = parseDate(formattedSelectedDate)"
              v-bind="attrs"
              v-on="on"
            ></v-text-field>
          </template>
          <v-date-picker v-model="selectedDate" locale="pt-br" color="#48B38B" scrollable>
            <v-spacer></v-spacer>
            <v-btn text color="primary" @click="isDatePickerOpen = false"> Cancelar </v-btn>
            <v-btn text color="primary" @click="updateSelectedDate(selectedDate)"> OK </v-btn>
          </v-date-picker>
        </v-dialog>
      </div>
      <div class="basic-information">
        <v-text-field
          @click="isClientSelectorModalActive = true"
          :v-model="selectedBudget?.client?.id"
          :value="selectedBudget?.client?.name"
          label="Cliente"
          append-icon="mdi-account"
          class="ma-0 pt-0"
          hide-details
          outlined
          readonly
        ></v-text-field>
      </div>
      <div class="services">
        <span class="s-title">Serviços</span>
        <div v-for="(service, index) in selectedBudget.budget_services" :key="index" class="service">
          <v-btn class="remove" icon @click="removeService(service)" color="red">
            <v-icon small>mdi-close</v-icon>
          </v-btn>
          <div class="service-name-quantity">
            <div class="service-name">{{ service.service.description }}</div>
            <div class="service-quantity">{{ service.amount }}x {{ formatCurrency(service.total_value) }}</div>
          </div>
          <v-tooltip v-if="service.disaproved_by_client" bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-icon color="amber" dark class="mr-2" v-bind="attrs" v-on="on"> mdi-alert </v-icon>
            </template>
            <span>Serviço reprovado pelo cliente</span>
          </v-tooltip>
          <div class="actions">
            <v-btn class="quantity-btn" icon @click="decreaseQuantity(service, 'service')">
              <v-icon small>mdi-minus</v-icon>
            </v-btn>
            <span>{{ service.amount }}</span>
            <v-btn class="quantity-btn" icon @click="increaseQuantity(service, 'service')">
              <v-icon small>mdi-plus</v-icon>
            </v-btn>
            <v-btn class="more-btn" icon @click="openServiceManager(service)">
              <v-icon small>mdi-dots-vertical</v-icon>
            </v-btn>
          </div>
        </div>
        <v-btn block class="mt-2" color="primary" outlined dark depressed large @click="isServiceSelectorModalActive = true">
          <v-icon left>mdi-plus</v-icon>
          Adicionar serviço
        </v-btn>
      </div>
      <div class="products">
        <span class="p-title">Produtos</span>
        <div v-for="(product, index) in selectedBudget.budget_products" :key="index" class="product">
          <v-btn class="remove" icon @click="removeProduct(product)" color="red">
            <v-icon small>mdi-close</v-icon>
          </v-btn>
          <div class="product-name-quantity">
            <div class="product-name">{{ product.product.description }}</div>
            <div class="product-quantity">{{ product.amount }}x {{ formatCurrency(product.total_value) }}</div>
          </div>
          <v-tooltip v-if="product.disaproved_by_client" bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-icon color="amber" dark class="mr-2" v-bind="attrs" v-on="on"> mdi-alert </v-icon>
            </template>
            <span>Produto reprovado pelo cliente</span>
          </v-tooltip>
          <div class="actions">
            <v-btn class="quantity-btn" icon @click="decreaseQuantity(product, 'product')">
              <v-icon small>mdi-minus</v-icon>
            </v-btn>
            <span>{{ product.amount }}</span>
            <v-btn class="quantity-btn" icon @click="increaseQuantity(product, 'product')">
              <v-icon small>mdi-plus</v-icon>
            </v-btn>
            <v-btn class="more-btn" icon @click="openProductManager(product)">
              <v-icon small>mdi-dots-vertical</v-icon>
            </v-btn>
          </div>
        </div>
        <v-btn block color="primary" class="mt-2" outlined dark depressed large @click="isProductSelectorModalActive = true">
          <v-icon left>mdi-plus</v-icon>
          Adicionar produto
        </v-btn>
      </div>
      <div class="observation">
        <v-textarea
          v-model="observation"
          label="Observações gerais"
          append-icon="mdi-clipboard-text"
          hide-details
          rows="1"
          auto-grow
          outlined
          v-debounce:2s="() => $store.commit('Budget/UPDATE_OBSERVATION', observation)"
        ></v-textarea>
      </div>
      <div class="subtotal-discount-total">
        <div class="subtotal">
          <div class="label">Subtotal</div>
          <div class="value">{{ subTotal }}</div>
        </div>
        <div class="discount">
          <div class="label">Desconto</div>
          <div v-if="hasDiscount" class="value">
            {{ `${discount} (${this.calculateDiscountResume()})` }}
            <v-btn color="primary" text small outlined @click="isDiscountModalActive = true">
              <v-icon small>mdi-pencil</v-icon>
            </v-btn>
          </div>
          <div v-else class="value">
            <v-btn color="primary" text small outlined @click="isDiscountModalActive = true"> Aplicar desconto </v-btn>
          </div>
        </div>
        <div class="entry-value">
          <div class="label">Adiantamento</div>
          <div v-if="hasEntryValue" class="value">
            {{ entryValueDisplay }}
            <v-btn color="#48b38b" text small outlined @click="isEntryValueModalActive = true">
              <v-icon small>mdi-pencil</v-icon>
            </v-btn>
          </div>
          <div v-else class="value">
            <v-btn color="#48b38b" text small outlined @click="isEntryValueModalActive = true"> Informar adiantamento </v-btn>
          </div>
        </div>
        <v-divider></v-divider>
        <div class="total">
          <div class="label">Total</div>
          <div class="value">{{ total }}</div>
        </div>
        <div v-if="hasEntryValue" class="total-remaining">
          <div class="label">Restante a pagar</div>
          <div class="value entry-value-color">{{ totalRemaining }}</div>
        </div>
      </div>

      <v-card class="payment mx-auto">
        <v-list>
          <v-list-item-group>
            <v-list-item @click="isPaymentMethodModalActive = true">
              <v-list-item-content>
                <v-list-item-title style="color: rgba(0, 0, 0, 0.6) !important">Formas de Pagamento</v-list-item-title>
                <v-list-item-subtitle>{{ paymentMethods }}</v-list-item-subtitle>
              </v-list-item-content>
              <v-list-item-action>
                <v-icon>mdi-chevron-right</v-icon>
              </v-list-item-action>
            </v-list-item>

            <v-list-item @click="isPaymentConditionModalActive = true">
              <v-list-item-content>
                <v-list-item-title style="color: rgba(0, 0, 0, 0.6) !important">Condições de Pagamento</v-list-item-title>
                <v-list-item-subtitle>{{ paymentConditions }}</v-list-item-subtitle>
              </v-list-item-content>
              <v-list-item-action>
                <v-icon>mdi-chevron-right</v-icon>
              </v-list-item-action>
            </v-list-item>
          </v-list-item-group>
        </v-list>
      </v-card>

      <div class="attachments">
        <span class="a-title">Anexos</span>
        <AttachmentsManager
          :attachments="selectedBudget.budget_attachments"
          @rename="handleRenameAttachment"
          @upload="handleUploadAttachment"
          @delete="handleDeleteAttachment"
        />
      </div>
    </div>

    <v-dialog v-model="isRemoveItemDialogActive" persistent max-width="290">
      <v-card>
        <v-card-title class="text-h5"> Remover {{ itemToRemove ? (itemToRemove.type === 'service' ? 'serviço' : 'produto') : '' }} </v-card-title>
        <v-card-text>
          Tem certeza que deseja remover o {{ itemToRemove ? (itemToRemove.type === 'service' ? 'serviço' : 'produto') : '' }}
          <strong>{{ itemToRemove ? itemToRemove[itemToRemove.type][itemToRemove.type].description : '' }}</strong
          >?
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="grey darken-1" text @click="isRemoveItemDialogActive = false"> Cancelar </v-btn>
          <v-btn color="red darken-1" text @click="handleConfirmRemove"> Remover </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <ClientSelector :isActive="isClientSelectorModalActive" @close="isClientSelectorModalActive = false" @select="handleSelectClient" />

    <ServiceSelector :isActive="isServiceSelectorModalActive" @close="isServiceSelectorModalActive = false" @select="handleSelectServices" />

    <ProductSelector :isActive="isProductSelectorModalActive" @close="isProductSelectorModalActive = false" @select="handleSelectProducts" />

    <Discount
      :isActive="isDiscountModalActive"
      @close="isDiscountModalActive = false"
      @select="handleSelectDiscount"
      :total="subTotal"
      :totalServices="formatCurrency(servicesTotal)"
      :totalProducts="formatCurrency(productsTotal)"
    />

    <EntryValue
      :isActive="isEntryValueModalActive"
      @close="isEntryValueModalActive = false"
      :total="total"
    />

    <PaymentCondition :isActive="isPaymentConditionModalActive" @close="isPaymentConditionModalActive = false" :conditions="conditions" />

    <PaymentMethod :isActive="isPaymentMethodModalActive" @close="isPaymentMethodModalActive = false" :methods="methods" />

    <ModalManageProduct
      v-if="isModalManageProductActive"
      :isActive="isModalManageProductActive"
      :product="selectedProduct"
      @close="closeProductManager"
      @remove="removeProduct"
    />

    <ModalManageService
      v-if="isModalManageServiceActive"
      :isActive="isModalManageServiceActive"
      :service="selectedService"
      @close="closeServiceManager"
      @remove="removeService"
    />

    <v-bottom-sheet v-model="isModalRenameAttachmentActive" persistent>
      <div class="bottom-sheet-content">
        <div class="bottom-sheet-header">
          <span class="bottom-sheet-title">Renomear anexo</span>
          <v-btn icon @click="isModalRenameAttachmentActive = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </div>
        <v-divider></v-divider>
        <v-text-field v-model="attachmentToRename.description" label="Nome do anexo" outlined clearable></v-text-field>
        <v-btn color="primary" block @click="renameAttachment" :disabled="!attachmentToRename.description || isLoading">Renomear</v-btn>
      </div>
    </v-bottom-sheet>

    <v-dialog v-model="isRemoveAttachmentModalActive" persistent max-width="290">
      <v-card>
        <v-card-title class="text-h5"> Remover anexo </v-card-title>
        <v-card-text> Tem certeza que deseja remover este anexo? </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="grey darken-1" text @click="isRemoveAttachmentModalActive = false"> Cancelar </v-btn>
          <v-btn color="red darken-1" text @click="removeAttachment"> Excluir </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-dialog>
</template>

<script>
import { mapState } from 'vuex';

import ServiceSelector from '@/components/Budget/ServiceSelector';
import ProductSelector from '@/components/Budget/ProductSelector';
import ClientSelector from '@/components/Client/ClientSelector';
import Discount from '@/components/Budget/Discount';
import EntryValue from '@/components/Budget/EntryValue';
import PaymentConditionService from '@/services/api/paymentCondition';
import PaymentMethodService from '@/services/api/paymentMethod';
import PaymentCondition from '@/components/Budget/PaymentCondition';
import PaymentMethod from '@/components/Budget/PaymentMethod';
import ModalManageProduct from '@/components/Budget/ModalManageProduct';
import ModalManageService from '@/components/Budget/ModalManageService';
import AttachmentsManager from '@/components/Budget/AttachmentsManager';
import BudgetAttachmentsService from '@/services/api/budgetAttachments';

export default {
  name: 'ManageBudget',
  emits: ['close'],
  components: {
    ClientSelector,
    ServiceSelector,
    ProductSelector,
    Discount,
    EntryValue,
    PaymentCondition,
    PaymentMethod,
    ModalManageProduct,
    ModalManageService,
    AttachmentsManager
  },
  props: {
    isActive: {
      type: Boolean,
      default: false
    },
    budget: {
      type: Object,
      default: null
    }
  },
  data() {
    return {
      isLoading: false,
      isDatePickerOpen: false,
      isClientSelectorModalActive: false,
      isProductSelectorModalActive: false,
      isServiceSelectorModalActive: false,
      isDiscountModalActive: false,
      isEntryValueModalActive: false,
      isRemoveItemDialogActive: false,
      isPaymentConditionModalActive: false,
      isPaymentMethodModalActive: false,
      isModalManageProductActive: false,
      isModalManageServiceActive: false,
      isModalRenameAttachmentActive: false,
      isRemoveAttachmentModalActive: false,
      selectedDate: null,
      formattedSelectedDate: null,
      discount: null,
      total: null,
      itemToRemove: null,
      observation: null,
      conditions: [],
      methods: [],
      selectedProduct: null,
      selectedService: null,
      attachmentToRename: {
        id: null,
        description: null
      },
      paymentMethods: null,
      paymentConditions: null
    };
  },
  created() {
    PaymentConditionService.search()
      .then((response) => {
        this.conditions = response.data;
      })
      .catch((error) => {
        console.log(error);
      })
      .finally(() => {
        this.isLoading = false;
      });

    PaymentMethodService.search()
      .then((response) => {
        this.methods = response.data;
      })
      .catch((error) => {
        console.log(error);
      })
      .finally(() => {
        this.isLoading = false;
      });
  },
  computed: {
    ...mapState('Budget', ['selectedBudget']),
    isNew() {
      return !this.selectedBudget;
    },
    titlePrefix() {
      return this.isNew ? 'Novo' : 'Editar';
    },
    isLegacyMode() {
      if (!this.selectedBudget) return true;

      const { discount, discount_type: discountType } = this.selectedBudget;

      const hasLegacyDiscount =
        discount !== null &&
        discount !== undefined &&
        discount !== '' &&
        discount !== 0 &&
        parseFloat(discount) > 0;

      const hasLegacyType =
        discountType !== null &&
        discountType !== undefined &&
        discountType !== '';

      return hasLegacyDiscount || hasLegacyType;
    },
    hasDiscount() {
      if (!this.selectedBudget) return false;
      if (this.isLegacyMode) {
        const discount = parseFloat(this.selectedBudget.discount) || 0;
        return discount > 0;
      }
      const discountServices = parseFloat(this.selectedBudget.discount_services) || 0;
      const discountProducts = parseFloat(this.selectedBudget.discount_products) || 0;
      return discountServices > 0 || discountProducts > 0;
    },
    servicesTotal() {
      if (!this.selectedBudget?.budget_services?.length) return 0;
      return this.selectedBudget.budget_services
        .filter((service) => service.disaproved_by_client !== true)
        .reduce((acc, service) => {
          const amount = parseInt(service.amount, 10) || 0;
          const unitaryValue = parseFloat(service.unitary_value) || 0;
          const totalValue = parseFloat(service.total_value) || 0;
          const calculatedTotal = amount * unitaryValue;
          return acc + (totalValue > 0 ? totalValue : calculatedTotal);
        }, 0);
    },
    productsTotal() {
      if (!this.selectedBudget?.budget_products?.length) return 0;
      return this.selectedBudget.budget_products
        .filter((product) => product.disaproved_by_client !== true)
        .reduce((acc, product) => {
          const amount = parseInt(product.amount, 10) || 0;
          const unitaryValue = parseFloat(product.unitary_value) || 0;
          const totalValue = parseFloat(product.total_value) || 0;
          const calculatedTotal = amount * unitaryValue;
          return acc + (totalValue > 0 ? totalValue : calculatedTotal);
        }, 0);
    },
    subTotal() {
      return this.formatCurrency(this.servicesTotal + this.productsTotal);
    },
    hasEntryValue() {
      if (!this.selectedBudget) return false;
      const entryValue = parseFloat(this.selectedBudget.entry_value) || 0;
      return entryValue > 0;
    },
    entryValueDisplay() {
      if (!this.hasEntryValue) return '';
      return this.formatCurrency(this.selectedBudget.entry_value);
    },
    totalRemaining() {
      const totalValue = this.parseCurrency(this.total);
      const entryValue = parseFloat(this.selectedBudget?.entry_value) || 0;
      return this.formatCurrency(totalValue - entryValue);
    }
  },
  watch: {
    selectedDate: {
      immediate: true,
      handler(newValue) {
        this.formattedSelectedDate = this.formatDate(newValue);
      }
    },
    selectedBudget: {
      immediate: true,
      handler(newValue) {
        this.selectedDate = newValue.date ? newValue.date.split('T')[0] : null;
        this.formattedSelectedDate = newValue.date ? this.formatDate(newValue.date.split('T')[0]) : '';

        this.discount = this.calculateDiscount();
        this.total = this.calculateTotal();

        this.observation = newValue.observation;

        this.paymentMethods = this.selectedBudget.budget_payment_methods
          ? this.selectedBudget.budget_payment_methods.map((method) => method.description).join(', ')
          : '';
        this.paymentConditions = this.selectedBudget.budget_payment_conditions
          ? this.selectedBudget.budget_payment_conditions.map((condition) => condition.description).join(', ')
          : '';
      },
      deep: true
    }
  },
  methods: {
    formatDate(date) {
      if (!date) return null;

      const [year, month, day] = date.split('-');
      return `${day}/${month}/${year}`;
    },
    parseDate(date) {
      if (!date) return null;

      const [day, month, year] = date.split('/');
      return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
    },
    calculateTotal() {
      const subTotal = this.parseCurrency(this.subTotal);
      const discount = this.calculateDiscount();

      return this.formatCurrency(subTotal - this.parseCurrency(discount));
    },
    calculateDiscount() {
      if (this.isLegacyMode) {
        if (!this.selectedBudget?.discount) return this.formatCurrency(0);

        const subTotal = this.parseCurrency(this.subTotal);
        const discountType = this.selectedBudget?.discount_type;
        const discount = this.selectedBudget?.discount;

        if (discount) {
          return discountType === 'percentual'
            ? this.formatCurrency(subTotal * (discount / 100))
            : this.formatCurrency(discount);
        }

        return this.formatCurrency(0);
      }

      // Novo modo: desconto separado por serviço e produto
      let totalDiscount = 0;

      const discountServices = parseFloat(this.selectedBudget.discount_services) || 0;
      const discountTypeServices = this.selectedBudget.discount_type_services;
      if (discountServices > 0) {
        totalDiscount +=
          discountTypeServices === 'percentual'
            ? this.servicesTotal * (discountServices / 100)
            : discountServices;
      }

      const discountProducts = parseFloat(this.selectedBudget.discount_products) || 0;
      const discountTypeProducts = this.selectedBudget.discount_type_products;
      if (discountProducts > 0) {
        totalDiscount +=
          discountTypeProducts === 'percentual'
            ? this.productsTotal * (discountProducts / 100)
            : discountProducts;
      }

      return this.formatCurrency(totalDiscount);
    },
    close() {
      this.$emit('close');
    },
    handleLog(message) {
      this.$store.commit('Budget/ADD_LOG', {
        description: message,
        createdAt: new Date().toISOString(),
        user: this.$store.getters['Auth/user']
      });
    },
    handleSelectServices(services) {
      this.$store.dispatch('Budget/addBudgetServices', { services });

      services.forEach((service) => {
        this.handleLog(`(1x) Serviço ${service.description} adicionado`);
      });
    },
    handleSelectProducts(products) {
      this.$store.dispatch('Budget/addBudgetProducts', { products });

      products.forEach((product) => {
        this.handleLog(`(1x) Produto ${product.description} adicionado`);
      });
    },
    handleSelectDiscount(discount) {
      this.$store.commit('Budget/UPDATE_DISCOUNT', discount);
    },
    decreaseQuantity(item, type) {
      if (item.amount > 1) {
        if (type === 'service') {
          const service = this.selectedBudget.budget_services.find((service) => service.id === item.id);
          this.$store.commit('Budget/UPDATE_BUDGET_SERVICE', { service, action: 'decrease' });
        } else if (type === 'product') {
          const product = this.selectedBudget.budget_products.find((product) => product.id === item.id);
          this.$store.commit('Budget/UPDATE_BUDGET_PRODUCT', { product, action: 'decrease' });
        }

        this.handleLog(`(${item.amount}x) ${type === 'service' ? 'Serviço' : 'Produto'} ${item[type].description} alterado`);
      }
    },
    increaseQuantity(item, type) {
      if (type === 'service') {
        const service = this.selectedBudget.budget_services.find((service) => service.id === item.id);
        this.$store.commit('Budget/UPDATE_BUDGET_SERVICE', { service, action: 'increase' });
      } else if (type === 'product') {
        const product = this.selectedBudget.budget_products.find((product) => product.id === item.id);
        this.$store.commit('Budget/UPDATE_BUDGET_PRODUCT', { product, action: 'increase' });
      }

      this.handleLog(`(${item.amount}x) ${type === 'service' ? 'Serviço' : 'Produto'} ${item[type].description} alterado`);
    },
    handleSelectClient(client) {
      this.$store.commit('Budget/UPDATE_CLIENT', client);
    },
    formatCurrency(value) {
      return value ? parseFloat(value).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }) : 'R$ 0,00';
    },
    parseCurrency(value) {
      return value ? parseFloat(value.replace('R$', '').replace('.', '').replace(',', '.').trim()) : 0;
    },
    removeService(service) {
      this.itemToRemove = { service, type: 'service' };

      if (this.selectedService) {
        this.handleConfirmRemove();

        this.selectedService = null;
        return;
      }

      this.isRemoveItemDialogActive = true;
    },
    removeProduct(product) {
      this.itemToRemove = { product, type: 'product' };

      if (this.selectedProduct) {
        this.handleConfirmRemove();

        this.selectedProduct = null;
        return;
      }

      this.isRemoveItemDialogActive = true;
    },
    updateSelectedDate(date) {
      this.isDatePickerOpen = false;

      const datetime = new Date(date);
      const formattedDatetime = datetime.toISOString();

      this.$store.commit('Budget/UPDATE_SELECTED_DATE', formattedDatetime);
    },
    handleConfirmRemove() {
      this.isRemoveItemDialogActive = false;

      if (this.itemToRemove.type === 'service') {
        this.$store.commit('Budget/REMOVE_BUDGET_SERVICE', this.itemToRemove.service);

        this.handleLog(`(${this.itemToRemove.service.amount}x) Serviço ${this.itemToRemove.service.service.description} removido`);
      } else if (this.itemToRemove.type === 'product') {
        this.$store.commit('Budget/REMOVE_BUDGET_PRODUCT', this.itemToRemove.product);

        this.handleLog(`(${this.itemToRemove.product.amount}x) Produto ${this.itemToRemove.product.product.description} removido`);
      }
    },
    calculateDiscountResume() {
      const subTotal = this.parseCurrency(this.subTotal);
      const discount = this.parseCurrency(this.calculateDiscount());

      const discountResume = subTotal > 0 ? (discount * 100) / subTotal : 0;

      return `${discountResume.toFixed(2)}%`;
    },
    openServiceManager(service) {
      this.selectedService = service;
      this.isModalManageServiceActive = true;
    },
    closeServiceManager() {
      this.selectedService = null;
      this.isModalManageServiceActive = false;
    },
    openProductManager(product) {
      this.selectedProduct = product;
      this.isModalManageProductActive = true;
    },
    closeProductManager() {
      this.selectedProduct = null;
      this.isModalManageProductActive = false;
    },
    handleRenameAttachment(attachment) {
      this.attachmentToRename = attachment;
      this.isModalRenameAttachmentActive = true;
    },
    renameAttachment() {
      this.isModalRenameAttachmentActive = false;
      this.isLoading = true;

      BudgetAttachmentsService.update(this.attachmentToRename.id, { description: this.attachmentToRename.description })
        .then((response) => {
          this.$store.commit('Budget/UPDATE_ATTACHMENT', { attachment: response.data });
        })
        .catch((error) => {
          console.log(error);
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    handleUploadAttachment(attachment) {
      this.isLoading = true;

      const formData = new FormData();
      formData.append('file', attachment);

      BudgetAttachmentsService.create(this.selectedBudget.id, formData)
        .then((response) => {
          this.$store.commit('Budget/ADD_LOG', {
            description: `Anexo ${response.data.description} adicionado`,
            createdAt: new Date().toISOString(),
            user: this.$store.getters['Auth/user']
          });

          this.$store.commit('Budget/ADD_ATTACHMENT', { attachment: response.data });
        })
        .catch((error) => {
          console.log(error);
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    handleDeleteAttachment(attachment) {
      this.attachmentToRename = attachment;
      this.isRemoveAttachmentModalActive = true;
    },
    removeAttachment() {
      this.isRemoveAttachmentModalActive = false;
      this.isLoading = true;

      BudgetAttachmentsService.inactivate(this.selectedBudget.id, this.attachmentToRename.id)
        .then(() => {
          this.$store.commit('Budget/ADD_LOG', {
            description: `Anexo ${this.attachmentToRename.description} removido`,
            createdAt: new Date().toISOString(),
            user: this.$store.getters['Auth/user']
          });

          this.$store.commit('Budget/REMOVE_ATTACHMENT', { attachment: this.attachmentToRename });
        })
        .catch((error) => {
          console.log(error);
        })
        .finally(() => {
          this.isLoading = false;
          this.attachmentToRename = {
            id: null,
            description: null
          };
        });
    }
  }
};
</script>

<style lang="scss" scoped>
.budget-details {
  color: rgba(0, 0, 0, 0.6) !important;
  background-color: #f0eff5 !important;
  overflow-y: auto;
  padding-top: 69px;
}

.top-bar {
  top: 0;
  width: 100%;
  z-index: 999;
  overflow: hidden;
  padding: 4px 10px;
  background-color: #fff;
  border-bottom: 1px solid #d9d8de;
  position: absolute;

  .action {
    border: 1px solid #c8c8c8;
    padding: 8px;
    margin: 8px;
    float: left;
    border-radius: 6px;
  }
}

.basic-information,
.services,
.products,
.attachments,
.subtotal-discount-total {
  background: white;
  padding: 20px !important;
  margin-top: 10px;
}

.subtotal-discount-total {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-bottom: 10px;

  .subtotal,
  .discount,
  .entry-value,
  .total,
  .total-remaining {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  .total {
    font-size: 18px;
    font-weight: 500;
  }

  .total-remaining {
    .label {
      color: #718096;
      font-size: 13px;
    }

    .value {
      font-weight: 700;
      font-size: 13px;
    }
  }

  .entry-value-color {
    color: #48b38b !important;
  }
}

.payment {
  margin-bottom: 10px;
  border-radius: 0 !important;
  box-shadow: none !important;
}

.observation {
  background: white;
  padding: 20px !important;
  margin-top: 10px;
  margin-bottom: 10px;
}

.attachments {
  margin-bottom: 10px;
}

.services,
.products,
.attachments {
  display: flex;
  flex-direction: column;
  gap: 10px;

  .service,
  .product {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 6px;

    .service-name-quantity,
    .product-name-quantity {
      display: flex;
      flex-direction: column;
      flex: 1;
      margin-left: 10px;
      font-size: 14px;
    }
  }
}

.s-title,
.p-title,
.a-title {
  border-bottom: 1px solid rgba(0, 0, 0, 0.38);
  padding-bottom: 5px;
  font-size: 16px !important;
}

.service-name,
.product-name {
  flex: 1;
  font-weight: 500;
  color: rgba(0, 0, 0, 0.8) !important;
}

.service-quantity,
.product-quantity {
  display: flex;
  align-items: center;
  justify-content: space-between;
  color: rgba(0, 0, 0, 0.6) !important;
}

.actions {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;

  .quantity-btn {
    min-width: 0;
    padding: 5px;
    border-radius: 50%;
    border: 1px solid #ccc;
    color: #48b38b !important;
  }

  .more-btn {
    min-width: 0;
    padding: 5px;
    color: #48b38b !important;
  }

  .v-btn--icon {
    margin: 0 !important;
  }

  .v-btn--icon.v-size--default {
    height: 24px !important;
    width: 24px !important;
  }
}

.client-info {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;

  .client-details {
    display: flex;
    flex-direction: column;
    flex: 1;
    font-size: 14px;

    .client-name {
      font-weight: 500;
      color: rgba(0, 0, 0, 0.8) !important;
      word-break: break-all;
    }

    .client-vehicle {
      color: rgba(0, 0, 0, 0.6) !important;
      word-break: break-all;
    }
  }

  .vehicle-plate {
    float: right;
    padding: 10px 8px;
    background-color: #eee;
    overflow: hidden;
    border-radius: 4px;
    width: 90px;

    img {
      float: left;
      width: 100%;
    }

    span {
      float: left;
      font-size: 12px;
      overflow: hidden;
      width: 100%;
      line-height: 19px;
      text-align: center;
      background-color: #eeeeee;
      border-bottom-right-radius: 5px;
      border-bottom-left-radius: 5px;
    }
  }
}
</style>
